import { legacyFavouriteSites } from "../favouriteSites/validate";
import { findDanglingReferences } from "../infra/audit/store";
import type { MarlinStoreDb } from "../infra/db";
import { addMachinesFromIpJsonSites } from "../sites/ipJson";
import { pruneLayoutReferences } from "../logic/layouts/references";
import { replaceRetiredScreenType } from "../logic/layouts/screenType";
import { SELECTED_CAMERA_KEY } from "../infra/state";
import {
  seedVideoSettings,
  withoutLegacyRenderDetections,
  type LegacyVideoSettingsSources,
} from "../settings/store";
import type { IpJsonContent, LegacyConfig, ScreenLayout } from "../types";

export const ADOPTION_KEY = "adoption.electronStore.v1";
export const IP_JSON_ADOPTION_KEY = "adoption.ipJson.v1";

export type AdoptionResult = {
  adopted: boolean;
  reason: "flagged" | "store-not-empty" | "imported" | "failed";
  dropped: string[];
  error?: string;
};

const failureMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

export const markLegacyConfigFailed = async (
  db: MarlinStoreDb,
  error: unknown,
): Promise<AdoptionResult> => {
  const message = failureMessage(error);
  await db.settings
    .put({
      key: ADOPTION_KEY,
      value: { at: Date.now(), imported: false, failed: true, error: message },
    })
    .catch(() => undefined);
  return { adopted: false, reason: "failed", dropped: [], error: message };
};

export const markLegacyIpJsonFailed = async (
  db: MarlinStoreDb,
  error: unknown,
): Promise<string> => {
  const message = failureMessage(error);
  await db.settings
    .put({
      key: IP_JSON_ADOPTION_KEY,
      value: { at: Date.now(), failed: true, error: message },
    })
    .catch(() => undefined);
  return message;
};

const SETTINGS_KEYS = [
  "datarouter",
  "ipJson",
  "autoUpdateSettings",
  "lastSeenVersion",
  "feedSettings",
  "gamepadSettings",
  "keyboardSettings",
  "selectedMultiScreenTemplateId",
  "selectedScreenTemplateIds",
] as const;

const adoptVideoSettings = async (
  db: MarlinStoreDb,
  config: LegacyConfig,
): Promise<void> => {
  const sources = config as LegacyVideoSettingsSources;
  const videoSettings = seedVideoSettings(sources) ?? sources.videoSettings;
  if (videoSettings)
    await db.settings.put({ key: "videoSettings", value: videoSettings });
  if (sources.notificationSettings)
    await db.settings.put({
      key: "notificationSettings",
      value: withoutLegacyRenderDetections(sources.notificationSettings),
    });
};

const storeHasData = async (db: MarlinStoreDb): Promise<boolean> => {
  for (const table of [
    db.machines,
    db.screens,
    db.templates,
    db.screenTemplates,
    db.quickActions,
    db.notifications,
  ])
    if ((await table.count()) > 0) return true;
  return false;
};

type AdoptionMarker = {
  at: number;
  imported?: boolean;
  failed?: boolean;
  source?: string;
  state?: string;
};

const fingerprint = (value: unknown): string =>
  JSON.stringify(value, (_key, item) =>
    item && typeof item === "object" && !Array.isArray(item)
      ? Object.fromEntries(
          Object.entries(item).sort(([a], [b]) => a.localeCompare(b)),
        )
      : item,
  );

const legacyConfigFingerprint = (config: LegacyConfig): string => {
  const source = fingerprint(config);
  let hash = 0xcbf29ce484222325n;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= BigInt(source.charCodeAt(index));
    hash = BigInt.asUintN(64, hash * 0x100000001b3n);
  }
  return hash.toString(16);
};

const getMarker = async (
  db: MarlinStoreDb,
): Promise<AdoptionMarker | undefined> =>
  (await db.settings.get(ADOPTION_KEY))?.value as AdoptionMarker | undefined;

const adoptionState = async (db: MarlinStoreDb): Promise<string> => {
  const state: Record<string, unknown> = {};
  for (const table of db.tables) {
    const rows = await table.toArray();
    state[table.name] =
      table.name === "settings"
        ? rows.filter(
            (row) =>
              row.key !== ADOPTION_KEY && row.key !== IP_JSON_ADOPTION_KEY,
          )
        : rows;
  }
  return fingerprint(state);
};

export const recordLegacyAdoptionState = (db: MarlinStoreDb): Promise<void> =>
  db.transaction("rw", db.tables, async () => {
    const marker = await getMarker(db);
    if (marker?.imported && marker.source)
      await db.settings.put({
        key: ADOPTION_KEY,
        value: { ...marker, state: await adoptionState(db) },
      });
  });

export const adoptLegacyConfig = async (
  db: MarlinStoreDb,
  config: LegacyConfig,
): Promise<AdoptionResult> => {
  const source = legacyConfigFingerprint(config);
  config = structuredClone(config);
  const dropped: string[] = [];
  return db.transaction("rw", db.tables, async () => {
    const marker = await getMarker(db);
    if (marker && !marker.failed) {
      if (
        !marker.imported ||
        !marker.source ||
        !marker.state ||
        marker.source === source ||
        marker.state !== (await adoptionState(db))
      )
        return { adopted: false, reason: "flagged" as const, dropped };
      for (const table of db.tables) await table.clear();
    } else if (await storeHasData(db)) {
      await db.settings.put({
        key: ADOPTION_KEY,
        value: { at: Date.now(), imported: false },
      });
      return { adopted: false, reason: "store-not-empty" as const, dropped };
    }

    const machineIds = new Set<number>();
    for (const site of Object.values(config.machines ?? {})) {
      machineIds.add(site.machine_id);
      await db.machines.put({
        ...site,
        cages: config.machinesLoaded?.[site.machine_id] ?? [],
      });
    }
    const siteIds = new Set(
      (await db.machines.toArray()).map((m) => m.site_id),
    );

    const cleanLayout = (layout: ScreenLayout, label: string) => {
      delete (layout as ScreenLayout & { order?: number }).order;
      layout.screenType = replaceRetiredScreenType(
        layout.screenType,
      ) as ScreenLayout["screenType"];
      return pruneLayoutReferences(layout, {
        keepMachine: (id) => machineIds.has(id),
        keepSite: (id) => siteIds.has(id),
        dropped: (path, kind, id) =>
          dropped.push(`${label}.${path}: unknown ${kind} ${id}`),
      });
    };
    for (const layout of config.screenLayouts ?? [])
      await db.screens.put(cleanLayout(layout, `screens[${layout.id}]`));
    for (const template of config.multiScreenLayoutTemplates ?? []) {
      for (const layout of template.screenLayouts)
        cleanLayout(layout, `templates[${template.id}].screens[${layout.id}]`);
      await db.templates.put(template);
    }
    for (const template of config.screenLayoutTemplates ?? []) {
      cleanLayout(template.screenLayout, `screenTemplates[${template.id}]`);
      await db.screenTemplates.put(template);
    }

    for (const [index, action] of (config.quickActions ?? []).entries()) {
      action.sites = (action.sites ?? []).filter((id) => {
        if (siteIds.has(id)) return true;
        dropped.push(`quickActions[${action.id}].sites: unknown site ${id}`);
        return false;
      });
      for (const step of action.steps ?? [])
        step.targets = (step.targets ?? []).filter((target) => {
          if (machineIds.has(target.machineId) && siteIds.has(target.siteId))
            return true;
          dropped.push(
            `quickActions[${action.id}].targets: unknown machine ${target.machineId}`,
          );
          return false;
        });
      await db.quickActions.put({ ...action, createdAt: index + 1 });
    }

    for (const notification of [...(config.notifications ?? [])].reverse())
      await db.notifications.add(notification);

    for (const [rawId, topics] of Object.entries(
      config.subscriptionOffsets ?? {},
    )) {
      const machineId = Number(rawId);
      if (!machineIds.has(machineId)) {
        dropped.push(
          `subscriptionOffsets[${rawId}]: unknown machine ${machineId}`,
        );
        continue;
      }
      for (const [topic, offset] of Object.entries(topics))
        await db.offsets.put({ machineId, topic, offset: Number(offset) });
    }

    let progressIndex = 0;
    for (const [actionId, executions] of Object.entries(
      config.quickActionProgress?.activeExecutions ?? {},
    ))
      for (const execution of executions) {
        if (!machineIds.has(execution.machineId)) {
          dropped.push(
            `activeExecutions[${actionId}]: unknown machine ${execution.machineId}`,
          );
          continue;
        }
        progressIndex += 1;
        await db.progress.put({
          ...execution,
          actionId,
          createdAt: progressIndex,
        });
      }

    const favourites = legacyFavouriteSites(config.favouriteSites);
    for (const cognitoSub of favourites.malformed)
      dropped.push(`favouriteSites[${cognitoSub}]: not a list of site ids`);
    await db.favouriteSites.bulkPut(favourites.rows);

    if (config.selected && machineIds.has(config.selected.machineId))
      await db.settings.put({
        key: SELECTED_CAMERA_KEY,
        value: config.selected,
      });

    for (const key of SETTINGS_KEYS)
      if (Object.prototype.hasOwnProperty.call(config, key))
        await db.settings.put({ key, value: config[key] });
    await adoptVideoSettings(db, config);

    const dangling = (await findDanglingReferences(db)).filter(
      (reference) => reference.table !== "notifications",
    );
    if (dangling.length > 0)
      throw new Error(
        `adoption produced dangling references, refusing to commit: ${JSON.stringify(dangling)}`,
      );

    await db.settings.put({
      key: ADOPTION_KEY,
      value: {
        at: Date.now(),
        imported: true,
        source,
        state: await adoptionState(db),
      },
    });
    return { adopted: true, reason: "imported" as const, dropped };
  });
};

const withIpJsonDefaults = (content: IpJsonContent): IpJsonContent => ({
  SCREENS: [],
  SELECTED_SITE: 0,
  ...content,
});

const replacesStoredIpJson = async (
  db: MarlinStoreDb,
  next: IpJsonContent,
): Promise<boolean> => {
  const existing = (await db.settings.get("ipJson"))?.value as
    | IpJsonContent
    | undefined;
  if (!Array.isArray(existing?.SITES) || existing.SITES.length === 0)
    return true;
  return fingerprint(withIpJsonDefaults(existing)) !== fingerprint(next);
};

export const importLegacyIpJson = async (
  db: MarlinStoreDb,
  content: IpJsonContent | null,
): Promise<boolean> => {
  if (!content || !Array.isArray(content.SITES) || content.SITES.length === 0)
    return false;
  const sites = content.SITES;
  const next = withIpJsonDefaults(content);
  if (!(await replacesStoredIpJson(db, next))) return false;
  return db.transaction("rw", [db.settings, db.machines], async () => {
    if (!(await replacesStoredIpJson(db, next))) return false;
    await db.settings.put({ key: "ipJson", value: next });
    await addMachinesFromIpJsonSites(db, sites);
    await db.settings.put({
      key: IP_JSON_ADOPTION_KEY,
      value: { at: Date.now() },
    });
    return true;
  });
};

export const snapshotTables = (
  db: MarlinStoreDb,
): Promise<Record<string, unknown[]>> =>
  db.transaction("r", db.tables, async () =>
    Object.fromEntries(
      await Promise.all(
        db.tables.map(async (table) => [table.name, await table.toArray()]),
      ),
    ),
  );
